import Vue from 'vue';
import Vuex from 'vuex';
import VuexPersistence from 'vuex-persist';
import PlacenamesService from './services/PlacenamesService';
import { Placename } from './models/Placename';
import { User } from './models/User';

Vue.use(Vuex);

const vuexLocal = new VuexPersistence({
  storage: window.localStorage,
  reducer: (state: any) => ({
    // add any states that need to be persisted beyond the current session
    user: state.user,
  }),
});

export default new Vuex.Store({
  state: {
    rootUrl: 'https://placenames.mapdev.ca/',
    // rootUrl: 'http://localhost:8080/',

    // LayoutDefault
    leftDrawerOpen: false,
    rightDrawerOpen: false,
    isMobile: false,

    // Window size
    windowHeight: null,
    windowWidth: null,

    // Login
    user: null,

    // controls which component is shown in the panel
    rightDrawerContent: {
      edit: false,
      print: false,
      about: false,
      layer: false,
    },

    leftDrawerContent: {
      popup: false,
      edit: false,
      approvals: false,
    },

    sketch: false, // visibility of sketch widget
    sketchLayer: null, // initalized as an empty GraphicsLayer

    // Layers
    layers: [
      {
        // Unapproved only shown in admin
        name: 'Mi\'kmaw Place Names Atlas [Unapproved]',
        type: 'FeatureLayer',
        url: 'https://services2.arcgis.com/hhEghZifbm8vjGX2/arcgis/rest/services/Placenames_Unapproved2/FeatureServer/0',
        active: true,
        unapproved: true,
      },
      {
        name: 'Mi\'kmaw Place Names Atlas',
        type: 'FeatureLayer',
        url: 'https://services2.arcgis.com/hhEghZifbm8vjGX2/arcgis/rest/services/Placenames_Public2/FeatureServer/0',
        active: true,
        search: true,
      },
      // {
      //   name: 'Address',
      //   type: 'FeatureLayer',
      //   url: 'https://nsgiwa.novascotia.ca/arcgis/rest/services/BASE/BASE_NS_CivicAddress_File_WM84/MapServer/0',
      //   active: false,
      //   minScale: 15000,
      // },
    ],
    basemaps: [
      {name: 'Topographic', url: 'topo-vector', active: true},
      {name: 'Satellite', url: 'satellite', active: false},
      {name: 'Streets', url: 'streets-vector', active: false},
      {name: 'Greyscale', url: 'gray-vector', active: false},
      {name: 'Dark Greyscale', url: 'dark-gray-vector', active: false},
    ],

    // domain code=>description
    sources : {
      PACIF: 'Father Pacifique Guide to Micmac Place Names',
      PIERS: 'Harry Piers unpublished notes',
      RAND: 'Silas Rand English to Micmac Dictionary',
      OTH: 'Other',
    },

    // search layer must also exist in the layers data
    search: {
      // url not needed anymore, specify search=true in layers
      // url: 'https://services2.arcgis.com/hhEghZifbm8vjGX2/arcgis/rest/services/Placenames_Public/FeatureServer/0',
      layer: null, // gets populated after created
      layerView: null,
    },

    // this is municipality, but the counties are polylines
    communitiesLayer: {
        type: 'FeatureLayer',
        url: 'https://nsgiwa.novascotia.ca/arcgis/rest/services/BND/BND_NS_Community_Bndys_UT83/MapServer/0',
    },

    unapproved: {
      count: null,
      data: [], // queried features from layer
      layer: null, // gets populated after created
      layerView: null,
    },

    edit: {
      // editing is done though web api now
      // https://services2.arcgis.com/hhEghZifbm8vjGX2/arcgis/rest/services/Placenames_Editor/FeatureServer/0
      url: null, // 'https://services1.arcgis.com/l7yyY2A6SvFv23CL/arcgis/rest/services/Mikmaw_Placenames_EDIT/FeatureServer/0',
      layer: null, // gets populated after created
      layerView: null,
    },

    // placeholder for feature that is being edited
    editFeature: null,

    // EsriMap
    map: null,
    mapView: null,
    center: null,
    zoom: null,
    searchLayer: null,

    // NS Locator
    novaScotiaLocator: {
      url: 'https://utility.arcgis.com/usrsvcs/servers/5cf2b7b81d0342fc9977c7868ab32f5e/rest/services/World/GeocodeServer',
      name: 'Nova Scotia Geocoder',
      placeholder: 'Find address or place',
    },

  },

  mutations: {
    LOGIN(state: any, user: User | null) {
      state.user = user;
    },
    SKETCH_TOGGLE(state: any) {
      state.sketch = !state.sketch;
    },
    DRAWER_TOGGLE_LEFT(state: any, content: any) {
      for (const k of Object.keys(state.leftDrawerContent)) {
        if (k === content) {
          state.leftDrawerContent[k] = !state.leftDrawerContent[k];
        } else {
          state.leftDrawerContent[k] = false;
        }
      }
      let openSesame = false;
      for (const v of Object.values(state.leftDrawerContent)) {
        if (v) {
          openSesame = true;
        }
      }
      state.leftDrawerOpen = openSesame;
    },
    DRAWER_TOGGLE_RIGHT(state: any, content: string) {
      for (const k of Object.keys(state.rightDrawerContent)) {
        if (k === content) {
          state.rightDrawerContent[k] = !state.rightDrawerContent[k];
        } else {
          state.rightDrawerContent[k] = false;
        }
      }
      let openSesame = false;
      for (const v of Object.values(state.rightDrawerContent)) {
        if (v) {
          openSesame = true;
        }
      }
      state.rightDrawerOpen = openSesame;
    },
    SET_BASEMAP(state: any, basemap: string) {
      state.map.basemap = basemap;
      state.basemaps.forEach((item: any, index: number) => {
          if (item.url === basemap) {
              item.active = true;
          } else {
              item.active = false;
          }
      });
    },
    TOGGLE_LAYER(state: any, layer: any) {
      // this is not clear, should be using ids
      state.map.layers.forEach((item: any, index: number) => {
        if (layer.url.startsWith(item.url)) {
          item.visible = !item.visible;
        }
      });
    },
    SET_UNAPPROVED_COUNT(state: any, count: number) {
      state.unapproved.count = count;
    },
    SET_UNAPPROVED_DATA(state: any, data: any) {
      state.unapproved.data = data;
    },
    SET_EDIT_FEATURE(state: any, placename?: Placename) {
      state.editFeature = placename;
    },
    UPDATE_WINDOW_HEIGHT(state: any, windowHeight?: number) {
      state.windowHeight = windowHeight;
    },
    UPDATE_WINDOW_WIDTH(state: any, windowWidth?: number) {
      state.windowWidth = windowWidth;
    },
  },

  actions: {
    refreshToken(context, payload) {
      if (context.state.user) {
        context.state.user.refreshToken();
      }
    },
    login(context, payload) {
      console.log('login action');
      context.commit('LOGIN', payload);
    },
    drawerToggleLeft(context, content) {
      context.commit('DRAWER_TOGGLE_LEFT', content);
    },
    drawerToggleRight(context, content) {
      context.commit('DRAWER_TOGGLE_RIGHT', content);
    },
    setBasemap(context, basemap) {
      context.commit('SET_BASEMAP', basemap);
    },
    toggleLayer(context, layer) {
      context.commit('TOGGLE_LAYER', layer);
    },
    sketchToggle(context) {
      context.commit('SKETCH_TOGGLE');
    },
    initSearch(context) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    initPopup(context) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    popupFromSearch(context, feature) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    aboutAction(context) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    notify(context, message) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    getAudioFileNames(context) {
      PlacenamesService.getFileNames()
      .then((response: any) => {
        console.log(response.data);
      })
      .catch((error: any) => {
        console.error(error);
        if (error.response) {
          this.dispatch('notify', error.response);
        }
      });
    },
    deleteAudioFile(context, filename) {
      console.log(filename);
      PlacenamesService.deleteAudio(filename)
      .then((response: any) => {
        console.log(response.data);
      })
      .catch((error: any) => {
        console.error(error);
        if (error.response) {
          this.dispatch('notify', error.response);
        }
      });
    },
    // UNUSED - this is done directly in AddData component
    uploadAudioFile(context, file: File) {
      const formData = new FormData();
      formData.set('file', file);
      PlacenamesService.uploadAudio(formData)
      .then((response: any) => {
        // TODO handle this uploaded filename
        console.log(response.data);
      })
      .catch((error: any) => {
        console.error(error);
        if (error.response) {
          this.dispatch('notify', error.response);
        }
      });
    },
    deletePlacename(context, id: number) {
      PlacenamesService.deletePlacename(id)
      .then((response: any) => {
        console.log(response.data); // TODO: should be true
        this.dispatch('refreshLayers');
        this.dispatch('updateUnapprovedCount');
        this.dispatch('updateUnapprovedData');
      })
      .catch((error: any) => {
        console.error(error);
        if (error.response) {
          this.dispatch('notify', error.response);
        }
      });
    },
    addPlacename(context, placename: Placename) {
      PlacenamesService.upsertPlacename(placename)
      .then((response: any) => {
        console.log(response.data); // TODO: should be true
        // close the add data toolbar
        this.dispatch('drawerToggleLeft', 'nothing');
        context.commit('SET_EDIT_FEATURE', null);
        this.dispatch('refreshLayers');
        this.dispatch('updateUnapprovedCount');
        this.dispatch('updateUnapprovedData');
      })
      .catch((error: any) => {
        console.error(error);
        if (error.response) {
          this.dispatch('notify', error.response.statusText);
        }
      });
    },
    resetEditFeature(context) {
      context.commit('SET_EDIT_FEATURE', null);
    },
    async editPlacename(context, id: number) {
      // get placename, set editFeature
      const query = this.state.unapproved.layer.createQuery();
      query.returnGeometry = true;
      query.outFields = ['*'];
      query.where = 'OBJECTID = ' + id;
      // check both unaprooved and approved views for data
      let editPlacename = await this.state.unapproved.layer.queryFeatures(query).then((results: __esri.FeatureSet) => {
        console.log(results.features);
        if (results.features.length) {
          const placename = new Placename(results.features[0].attributes);
          placename.x = (results.features[0].geometry as __esri.Point).x;
          placename.y = (results.features[0].geometry as __esri.Point).y;
          return placename;
        }
      });

      if (!editPlacename) {
        editPlacename = await this.state.search.layer.queryFeatures(query).then((results: __esri.FeatureSet) => {
          console.log(results.features);
          if (results.features.length) {
            const placename = new Placename(results.features[0].attributes);
            placename.x = (results.features[0].geometry as __esri.Point).x;
            placename.y = (results.features[0].geometry as __esri.Point).y;
            return placename;
          }
        });
      }

      if (!editPlacename) {
        context.commit('SET_EDIT_FEATURE', null);
        context.dispatch('notify', 'Editable feature not found');
      } else {
        context.commit('SET_EDIT_FEATURE', editPlacename);
        this.dispatch('drawerToggleLeft', 'edit');
      }

    },
    approvePlacename(context, id: number) {
      PlacenamesService.approve(id)
      .then((response: any) => {
        console.log(response.data);
        this.dispatch('refreshLayers');
        this.dispatch('updateUnapprovedCount');
        this.dispatch('updateUnapprovedData');
      })
      .catch((error: any) => {
        console.error(error);
        if (error.response) {
          this.dispatch('notify', error.response);
        }
      });
    },
    rejectPlacename(context, id: number) {
      PlacenamesService.reject(id)
      .then((response: any) => {
        console.log(response.data); // TODO: should be true
        this.dispatch('refreshLayers');
        this.dispatch('updateUnapprovedCount');
        this.dispatch('updateUnapprovedData');
      })
      .catch((error: any) => {
        console.error(error);
        if (error.response) {
          this.dispatch('notify', error.response);
        }
      });
    },
    refreshLayers(context) {
      console.log('refreshLayers');
      this.state.mapView.popup.close();
      if (this.state.unapproved.layer) {
        this.state.unapproved.layer.refresh();
      }
      if (this.state.search.layer) {
        this.state.search.layer.refresh();
      }
    },
    updateUnapprovedCount(context) {
      if (this.state.unapproved.layer) {
        this.state.unapproved.layer.queryFeatures().then((results: any) => {
            context.commit('SET_UNAPPROVED_COUNT', results.features.length);
        });
      }
    },
    updateUnapprovedData(context, data) {
      if (this.state.unapproved.layer) {
        const query = this.state.unapproved.layer.createQuery();
        query.returnGeometry = true;
        query.outFields = ['*'];
        this.state.unapproved.layer.queryFeatures(query).then((results: __esri.FeatureSet) => {
            console.log(results.features);
            console.log(results.features.length);
            const allAttributes: any = [];
            for (const feature of results.features) {
              const placename = new Placename(feature.attributes);
              placename.x = (feature.geometry as __esri.Point).x;
              placename.y = (feature.geometry as __esri.Point).y;
              allAttributes.push(placename);
            }
            context.commit('SET_UNAPPROVED_DATA', allAttributes);
        });
      }
    },
    updateWindowHeight(context, windowHeight) {
      context.commit('UPDATE_WINDOW_HEIGHT', windowHeight);
    },
    updateWindowWidth(context, windowWidth) {
      context.commit('UPDATE_WINDOW_WIDTH', windowWidth);
    },
  },

  getters: {
    rootUrl: (state) => {
      return state.rootUrl;
    },
    audioRootUrl: (state) => {
      if (state.rootUrl.indexOf('localhost') !== -1) {
        return state.rootUrl + 'admin/audio/';
      }
      return state.rootUrl + 'audio/';
    },
    token: (state) => {
      if (state.user && state.user.token) {
        return state.user.token;
      }
      return '';
    },
    user: (state) => {
      return state.user;
    },
  },

  plugins: [vuexLocal.plugin],
});
